import { FC, ReactNode, useId, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  PopoverOrigin,
  useTheme,
} from "@mui/material";

import { CheckIcon } from "../../icons/CheckIcon";
import ChevronDownIcon from "../../icons/ChevronDownIcon";

export interface CoreDropdownOption {
  value: string;
  label: string;
  selected?: boolean;
  /** Rendered at the row's end, e.g. a delete button; receives `close` like onClick. */
  endAdornment?: (close: () => void) => ReactNode;
}

export interface CoreDropdownProps {
  label: string;
  ariaLabel?: string;
  icon?: ReactNode;
  bordered?: boolean;
  width?: string | number;
  disabled?: boolean;
  disableOnNoOptions?: boolean;
  loading?: boolean;
  menuId?: string;
  menuMinWidth?: number | string;
  menuMaxWidth?: number | string;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  options: CoreDropdownOption[];
  /** Value(s) considered selected; used to check the matching option(s) when it isn't flagged via `option.selected`. */
  selected?: string | string[];
  onChange?: (value: string) => void;
}

/**
 * Button-triggered Menu with trigger styling and open/close plumbing factored out
 * so other data-driven dropdowns (rows rendered from `options`) don't have to
 * hand-roll the same Button/Menu/MenuItem pattern -- currently used by
 * ContractResearchFilters; not yet adopted by similar dropdowns elsewhere
 * (e.g. ClaimsFilterViewsDropdown still hand-rolls it).
 */
const CoreDropdown: FC<CoreDropdownProps> = ({
  label,
  ariaLabel,
  icon,
  bordered = true,
  width = "auto",
  disabled = false,
  disableOnNoOptions = false,
  loading = false,
  menuId,
  menuMinWidth = 300,
  menuMaxWidth = 420,
  anchorOrigin = { vertical: "bottom", horizontal: "left" },
  transformOrigin = { vertical: "top", horizontal: "left" },
  options,
  selected,
  onChange,
}) => {
  const theme = useTheme();
  const generatedMenuId = useId();
  const resolvedMenuId = menuId ?? `core-dropdown-menu-${generatedMenuId}`;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const isDisabled =
    disabled || loading || (disableOnNoOptions && options?.length === 0);

  // Surface the active selection in the trigger itself — otherwise "State"/"Plan
  // Name" look identical whether or not a filter is applied, and the only sign
  // of an active filter is the separate "Clear Filters" button.
  const selectedLabels = options
    .filter((option) =>
      option.selected ??
      (Array.isArray(selected)
        ? selected.includes(option.value)
        : selected === option.value),
    )
    .map((option) => option.label);
  const triggerLabel =
    selectedLabels.length > 0 ? `${label}: ${selectedLabels.join(", ")}` : label;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onClick = (option: CoreDropdownOption) => {
    if (!onChange) {
      return;
    }

    onChange(option.value);
    handleClose();
  };

  return (
    <>
      <Button
        variant="text"
        onClick={handleOpen}
        disabled={isDisabled}
        aria-label={ariaLabel ?? label}
        aria-controls={open ? resolvedMenuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        startIcon={
          loading ? (
            <CircularProgress size={14} thickness={5} />
          ) : (
            icon && (
              <Box
                component="span"
                aria-hidden
                sx={{
                  display: "inline-flex",
                  "& svg": { width: 18, height: 18 },
                }}
              >
                {icon}
              </Box>
            )
          )
        }
        endIcon={<ChevronDownIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 400,
          minWidth: "auto",
          width,
          whiteSpace: "nowrap",
          backgroundColor: theme.palette.background.paper,
          ...(bordered && {
            border: "1px solid",
            borderColor: theme.palette.divider,
            borderRadius: "8px",
            height: "36px",
            px: 1.5,
          }),
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        {triggerLabel}
      </Button>

      <Menu
        id={resolvedMenuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: menuMinWidth,
              maxWidth: menuMaxWidth,
              boxShadow: theme.shadows[8],
              border: `1px solid ${theme.palette.divider}`,
            },
          },
          list: { dense: true },
        }}
      >
        {options.length > 0 ? (
          options.map((option) => (
            <CoreDropdownItem
              key={option.value}
              label={option.label}
              selected={
                option.selected ??
                (Array.isArray(selected)
                  ? selected.includes(option.value)
                  : selected === option.value)
              }
              onClick={() => onClick(option)}
              endAdornment={option.endAdornment?.(handleClose)}
            />
          ))
        ) : (
          <MenuItem disabled>No data to display</MenuItem>
        )}
      </Menu>
    </>
  );
};

export default CoreDropdown;

export interface CoreDropdownItemProps {
  label: string;
  selected?: boolean;
  onClick?: () => void;
  /** Rendered at the row's end, e.g. a delete button; clicks/mousedowns don't reach the item. */
  endAdornment?: ReactNode;
}

/** Row for CoreDropdown's data-driven lists: optional selected check, label, trailing action. */
export const CoreDropdownItem: FC<CoreDropdownItemProps> = ({
  label,
  selected = false,
  onClick,
  endAdornment,
}) => (
  <MenuItem
    selected={selected}
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      py: 0.75,
      pr: 0.5,
    }}
  >
    <ListItemIcon sx={{ minWidth: "25px !important" }}>
      {selected ? (
        <CheckIcon size={18} />
      ) : (
        <Box sx={{ width: 18 }} aria-hidden />
      )}
    </ListItemIcon>
    <ListItemText
      primary={label}
      primaryTypographyProps={{
        noWrap: true,
        title: label,
      }}
      sx={{ flex: "1 1 auto", minWidth: 0, mr: 0.5 }}
    />

    {endAdornment && (
      <Box
        sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {endAdornment}
      </Box>
    )}
  </MenuItem>
);
